import { z } from "zod";
import { assertProjectCreateAccess, assertProjectWriteAccess, queueClockifyProjectSync, } from "./projects-helpers.js";
const uuidSchema = z.string().uuid();
const createProjectFromInputSchema = z.object({
    id: uuidSchema,
    name: z.string().trim().min(1).max(500),
    sourceProjectID: uuidSchema.nullable().optional(),
});
const copyQuoteInputSchema = z.object({
    targetProjectID: uuidSchema,
    targetQuoteID: uuidSchema,
    targetVersionID: uuidSchema,
    sourceQuoteID: uuidSchema,
    sourceVersionID: uuidSchema,
});
const copyCameraPlanInputSchema = z.object({
    targetProjectID: uuidSchema,
    targetPlanID: uuidSchema,
    sourcePlanID: uuidSchema,
});
const copyFunctionsInputSchema = z.object({
    targetProjectID: uuidSchema,
    sourceProjectID: uuidSchema,
});
const copyEquipmentPlanningInputSchema = z.object({
    targetProjectID: uuidSchema,
    sourceProjectID: uuidSchema,
    mapCameraAssignments: z.boolean().optional(),
});
function assertEquipmentWriteAccess(ctx) {
    if (ctx.role !== "admin" && ctx.role !== "employee") {
        throw new Error("Not allowed to edit equipment plans");
    }
}
function projectAnchor(project) {
    return (project?.eventStartDate ??
        project?.planningStartDate ??
        project?.startDate ??
        null);
}
function copyOffset(sourceProject, targetProject) {
    const sourceAnchor = projectAnchor(sourceProject);
    const targetAnchor = projectAnchor(targetProject);
    if (typeof sourceAnchor !== "number" || typeof targetAnchor !== "number") {
        throw new Error("Set event, planning, or project start dates on both projects before copying dated content.");
    }
    return targetAnchor - sourceAnchor;
}
function maybeCopyOffset(sourceProject, targetProject) {
    const sourceAnchor = projectAnchor(sourceProject);
    const targetAnchor = projectAnchor(targetProject);
    if (typeof sourceAnchor !== "number" || typeof targetAnchor !== "number") {
        return null;
    }
    return targetAnchor - sourceAnchor;
}
function shiftDate(value, offset) {
    return typeof value === "number" && typeof offset === "number"
        ? value + offset
        : null;
}
async function getProject(tx, appZql, projectID) {
    const project = await tx.run(appZql.projects.where("id", projectID).one());
    if (!project)
        throw new Error("Project not found");
    return project;
}
async function copyQuoteVersionContent(tx, appZql, sourceVersionID, targetVersionID) {
    const sourceGroups = await tx.run(appZql.quoteGroups
        .where("quoteVersionID", sourceVersionID)
        .orderBy("displayOrder", "asc"));
    for (const group of sourceGroups ?? []) {
        const targetGroupID = crypto.randomUUID();
        await tx.mutate.quoteGroups.insert({
            id: targetGroupID,
            quoteVersionID: targetVersionID,
            name: group.name,
            displayOrder: group.displayOrder,
            isOptional: group.isOptional,
            applyProjectDiscount: group.applyProjectDiscount,
        });
        const sourceItems = await tx.run(appZql.quoteLineItems
            .where("quoteGroupID", group.id)
            .orderBy("displayOrder", "asc"));
        for (const item of sourceItems ?? []) {
            await tx.mutate.quoteLineItems.insert({
                id: crypto.randomUUID(),
                quoteGroupID: targetGroupID,
                name: item.name,
                amount: item.amount,
                factor: item.factor,
                unitPrice: item.unitPrice,
                discount: item.discount,
                notes: item.notes,
                externalNote: item.externalNote,
                displayOrder: item.displayOrder,
                isTitle: item.isTitle,
                lineItemType: item.lineItemType,
            });
        }
    }
    const sourceDeductions = await tx.run(appZql.quoteDeductionItems
        .where("quoteVersionID", sourceVersionID)
        .orderBy("displayOrder", "asc"));
    for (const deduction of sourceDeductions ?? []) {
        await tx.mutate.quoteDeductionItems.insert({
            id: crypto.randomUUID(),
            quoteVersionID: targetVersionID,
            description: deduction.description,
            amount: deduction.amount,
            displayOrder: deduction.displayOrder,
        });
    }
}
export function createAppCopyMutators({ defineAppMutator, appZql, }) {
    return {
        copyActions: {
            createProjectFrom: defineAppMutator(createProjectFromInputSchema, async ({ tx, ctx, args, }) => {
                assertProjectCreateAccess(ctx);
                const sourceProject = args.sourceProjectID
                    ? await getProject(tx, appZql, args.sourceProjectID)
                    : null;
                if (sourceProject) {
                    await assertProjectWriteAccess(tx, ctx, sourceProject.id);
                }
                await tx.mutate.projects.insert({
                    id: args.id,
                    name: args.name,
                    customerID: sourceProject?.customerID ?? null,
                    contactID: sourceProject?.contactID ?? null,
                    previousEditionProjectID: sourceProject?.id ?? null,
                    accountManagerID: sourceProject?.accountManagerID ?? null,
                    status: "initial_contact",
                    types: sourceProject?.types ?? [],
                    conditions: sourceProject?.conditions ?? null,
                    language: sourceProject?.language ?? "de",
                    defaultVatRate: sourceProject?.defaultVatRate ?? "CH_8_1",
                    defaultDiscountPercent: sourceProject?.defaultDiscountPercent ?? 0,
                });
                queueClockifyProjectSync(ctx, args.id);
            }),
            copyQuote: defineAppMutator(copyQuoteInputSchema, async ({ tx, ctx, args, }) => {
                const targetProject = await getProject(tx, appZql, args.targetProjectID);
                await assertProjectWriteAccess(tx, ctx, args.targetProjectID);
                const sourceQuote = await tx.run(appZql.quotes.where("id", args.sourceQuoteID).one());
                if (!sourceQuote)
                    throw new Error("Source quote not found");
                await assertProjectWriteAccess(tx, ctx, sourceQuote.projectID);
                const sourceProject = await getProject(tx, appZql, sourceQuote.projectID);
                const sourceVersion = await tx.run(appZql.quoteVersions.where("id", args.sourceVersionID).one());
                if (!sourceVersion || sourceVersion.quoteID !== sourceQuote.id) {
                    throw new Error("Source quote version not found");
                }
                const offset = maybeCopyOffset(sourceProject, targetProject);
                await tx.mutate.quotes.insert({
                    id: args.targetQuoteID,
                    projectID: args.targetProjectID,
                    name: sourceQuote.name,
                    customerID: sourceQuote.customerID ?? null,
                    contactID: sourceQuote.contactID ?? null,
                });
                await tx.mutate.quoteVersions.insert({
                    id: args.targetVersionID,
                    quoteID: args.targetQuoteID,
                    versionNumber: 1,
                    versionLabel: "1",
                    internalName: sourceVersion.internalName ?? null,
                    quoteDate: shiftDate(sourceVersion.quoteDate, offset),
                    validUntil: shiftDate(sourceVersion.validUntil, offset),
                    validityDays: sourceVersion.validityDays,
                    isAccepted: false,
                    pdfURL: null,
                    projectDiscountPercent: sourceVersion.projectDiscountPercent,
                    vatRate: sourceVersion.vatRate,
                    content: { groups: [] },
                    status: "draft",
                    generalConditions: sourceVersion.generalConditions ?? null,
                });
                await copyQuoteVersionContent(tx, appZql, args.sourceVersionID, args.targetVersionID);
            }),
            copyCameraPlan: defineAppMutator(copyCameraPlanInputSchema, async ({ tx, ctx, args, }) => {
                assertEquipmentWriteAccess(ctx);
                const targetProject = await getProject(tx, appZql, args.targetProjectID);
                await assertProjectWriteAccess(tx, ctx, args.targetProjectID);
                const sourcePlan = await tx.run(appZql.cameraPlans.where("id", args.sourcePlanID).one());
                if (!sourcePlan)
                    throw new Error("Source camera plan not found");
                await assertProjectWriteAccess(tx, ctx, sourcePlan.projectID);
                const sourceProject = await getProject(tx, appZql, sourcePlan.projectID);
                const offset = maybeCopyOffset(sourceProject, targetProject);
                await tx.mutate.cameraPlans.insert({
                    id: args.targetPlanID,
                    projectID: args.targetProjectID,
                    name: sourcePlan.name,
                    description: sourcePlan.description ?? null,
                    mapType: sourcePlan.mapType,
                    imageURL: sourcePlan.imageURL ?? null,
                    embedURL: sourcePlan.embedURL ?? null,
                    mapCenterLng: sourcePlan.mapCenterLng ?? null,
                    mapCenterLat: sourcePlan.mapCenterLat ?? null,
                    mapZoom: sourcePlan.mapZoom ?? 14,
                    eventDate: shiftDate(sourcePlan.eventDate, offset),
                    sortOrder: sourcePlan.sortOrder ?? 0,
                });
                const columns = await tx.run(appZql.cameraPlanColumns
                    .where("cameraPlanID", args.sourcePlanID)
                    .orderBy("sortOrder", "asc"));
                for (const column of columns ?? []) {
                    await tx.mutate.cameraPlanColumns.insert({
                        id: crypto.randomUUID(),
                        cameraPlanID: args.targetPlanID,
                        name: column.name,
                        columnType: column.columnType,
                        options: column.options ?? [],
                        sortOrder: column.sortOrder ?? 0,
                    });
                }
                const cameras = await tx.run(appZql.cameras
                    .where("cameraPlanID", args.sourcePlanID)
                    .orderBy("sortOrder", "asc"));
                for (const camera of cameras ?? []) {
                    await tx.mutate.cameras.insert({
                        id: crypto.randomUUID(),
                        cameraPlanID: args.targetPlanID,
                        number: camera.number,
                        description: camera.description ?? null,
                        icon: camera.icon ?? "tripod",
                        positionX: camera.positionX ?? null,
                        positionY: camera.positionY ?? null,
                        rotation: camera.rotation ?? 0,
                        track: camera.track ?? null,
                        customValues: camera.customValues ?? {},
                        sortOrder: camera.sortOrder ?? 0,
                        color: camera.color ?? "#3b82f6",
                        equipmentGroupID: null,
                    });
                }
                const overlays = await tx.run(appZql.cameraPlanOverlays
                    .where("cameraPlanID", args.sourcePlanID)
                    .orderBy("sortOrder", "asc"));
                for (const overlay of overlays ?? []) {
                    await tx.mutate.cameraPlanOverlays.insert({
                        id: crypto.randomUUID(),
                        cameraPlanID: args.targetPlanID,
                        overlayType: overlay.overlayType,
                        positionX: overlay.positionX,
                        positionY: overlay.positionY,
                        width: overlay.width,
                        height: overlay.height,
                        rotation: overlay.rotation,
                        color: overlay.color,
                        opacity: overlay.opacity,
                        strokeWidth: overlay.strokeWidth,
                        fill: overlay.fill,
                        imageURL: overlay.imageURL ?? null,
                        label: overlay.label ?? null,
                        fontSize: overlay.fontSize,
                        points: overlay.points ?? null,
                        zIndex: overlay.zIndex,
                        sortOrder: overlay.sortOrder,
                        locked: overlay.locked,
                    });
                }
            }),
            copyFunctions: defineAppMutator(copyFunctionsInputSchema, async ({ tx, ctx, args, }) => {
                const targetProject = await getProject(tx, appZql, args.targetProjectID);
                const sourceProject = await getProject(tx, appZql, args.sourceProjectID);
                await assertProjectWriteAccess(tx, ctx, args.targetProjectID);
                await assertProjectWriteAccess(tx, ctx, args.sourceProjectID);
                const existingTargetSeries = await tx.run(appZql.projectFunctionSeries.where("projectID", args.targetProjectID));
                if ((existingTargetSeries ?? []).length > 0) {
                    throw new Error("Functions can only be copied into an empty plan");
                }
                const offset = copyOffset(sourceProject, targetProject);
                const sourceSeries = await tx.run(appZql.projectFunctionSeries
                    .where("projectID", args.sourceProjectID)
                    .orderBy("createdAt", "asc"));
                for (const series of sourceSeries ?? []) {
                    const targetSeriesID = crypto.randomUUID();
                    const selectedDays = (series.selectedDays ?? [])
                        .map((day) => shiftDate(day, offset))
                        .filter((day) => day !== null)
                        .sort((a, b) => a - b);
                    if (selectedDays.length === 0)
                        continue;
                    await tx.mutate.projectFunctionSeries.insert({
                        id: targetSeriesID,
                        projectID: args.targetProjectID,
                        name: series.name,
                        requiredCount: series.requiredCount,
                        selectedDays,
                        systemTag: series.systemTag ?? null,
                        locationType: series.locationType ?? null,
                    });
                    const targetDayBySourceID = new Map();
                    const sourceDays = await tx.run(appZql.projectFunctions
                        .where("seriesID", series.id)
                        .orderBy("functionDate", "asc"));
                    for (const sourceDay of sourceDays ?? []) {
                        const targetDay = shiftDate(sourceDay.functionDate, offset);
                        if (targetDay === null)
                            continue;
                        const targetDayID = crypto.randomUUID();
                        targetDayBySourceID.set(sourceDay.id, targetDayID);
                        await tx.mutate.projectFunctions.insert({
                            id: targetDayID,
                            projectID: args.targetProjectID,
                            seriesID: targetSeriesID,
                            functionDate: targetDay,
                            name: sourceDay.name,
                            requiredCount: sourceDay.requiredCount,
                            requiredCountOverride: sourceDay.requiredCountOverride ?? null,
                            startTime: sourceDay.startTime ?? null,
                            endTime: sourceDay.endTime ?? null,
                        });
                    }
                    for (const sourceDay of sourceDays ?? []) {
                        const targetDayID = targetDayBySourceID.get(sourceDay.id);
                        if (!targetDayID)
                            continue;
                        const assignments = await tx.run(appZql.functionAssignments.where("functionID", sourceDay.id));
                        for (const assignment of assignments ?? []) {
                            await tx.mutate.functionAssignments.insert({
                                id: crypto.randomUUID(),
                                functionID: targetDayID,
                                personID: assignment.personID,
                                remark: assignment.remark ?? null,
                            });
                        }
                    }
                }
            }),
            copyEquipmentPlanning: defineAppMutator(copyEquipmentPlanningInputSchema, async ({ tx, ctx, args, }) => {
                assertEquipmentWriteAccess(ctx);
                const targetProject = await getProject(tx, appZql, args.targetProjectID);
                const sourceProject = await getProject(tx, appZql, args.sourceProjectID);
                await assertProjectWriteAccess(tx, ctx, args.targetProjectID);
                await assertProjectWriteAccess(tx, ctx, args.sourceProjectID);
                const targetGroups = await tx.run(appZql.projectEquipmentGroups.where("projectID", args.targetProjectID));
                const sourceGroups = await tx.run(appZql.projectEquipmentGroups
                    .where("projectID", args.sourceProjectID)
                    .orderBy("displayOrder", "asc"));
                const groupIDBySourceID = new Map();
                const temporaryIDBySourceID = new Map();
                const baseDisplayOrder = (targetGroups ?? []).length;
                for (const [index, group] of (sourceGroups ?? []).entries()) {
                    const targetGroupID = crypto.randomUUID();
                    groupIDBySourceID.set(group.id, targetGroupID);
                    await tx.mutate.projectEquipmentGroups.insert({
                        id: targetGroupID,
                        projectID: args.targetProjectID,
                        name: group.name,
                        packingState: group.packingState,
                        riggingState: group.riggingState,
                        displayOrder: baseDisplayOrder + index,
                        isCollapsed: group.isCollapsed ?? false,
                    });
                    const assignments = await tx.run(appZql.projectEquipmentAssignments.where("groupID", group.id));
                    for (const assignment of assignments ?? []) {
                        let targetTemporaryID = null;
                        if (assignment.temporaryEquipmentID) {
                            targetTemporaryID =
                                temporaryIDBySourceID.get(assignment.temporaryEquipmentID) ??
                                    null;
                            if (!targetTemporaryID) {
                                const sourceTemporary = await tx.run(appZql.temporaryEquipment
                                    .where("id", assignment.temporaryEquipmentID)
                                    .one());
                                if (sourceTemporary) {
                                    targetTemporaryID = crypto.randomUUID();
                                    temporaryIDBySourceID.set(sourceTemporary.id, targetTemporaryID);
                                    await tx.mutate.temporaryEquipment.insert({
                                        id: targetTemporaryID,
                                        projectID: args.targetProjectID,
                                        name: sourceTemporary.name,
                                    });
                                }
                            }
                        }
                        await tx.mutate.projectEquipmentAssignments.insert({
                            id: crypto.randomUUID(),
                            groupID: targetGroupID,
                            equipmentID: assignment.equipmentID ?? null,
                            temporaryEquipmentID: targetTemporaryID,
                            amount: assignment.amount,
                            notes: assignment.notes ?? null,
                            cameraPlanColumnKey: assignment.cameraPlanColumnKey ?? null,
                        });
                    }
                }
                if (args.mapCameraAssignments ?? true) {
                    const offset = maybeCopyOffset(sourceProject, targetProject);
                    const sourcePlans = await tx.run(appZql.cameraPlans.where("projectID", args.sourceProjectID));
                    const targetPlans = await tx.run(appZql.cameraPlans.where("projectID", args.targetProjectID));
                    const sourcePlanByID = new Map((sourcePlans ?? []).map((plan) => [plan.id, plan]));
                    const targetPlanIDsByDate = new Map();
                    const targetPlanIDsByName = new Map();
                    for (const plan of targetPlans ?? []) {
                        if (typeof plan.eventDate === "number") {
                            const ids = targetPlanIDsByDate.get(plan.eventDate) ?? [];
                            ids.push(plan.id);
                            targetPlanIDsByDate.set(plan.eventDate, ids);
                        }
                        const name = String(plan.name).trim().toLowerCase();
                        const ids = targetPlanIDsByName.get(name) ?? [];
                        ids.push(plan.id);
                        targetPlanIDsByName.set(name, ids);
                    }
                    const targetCameras = await tx.run(appZql.cameras.whereExists("cameraPlan", (plan) => plan.where("projectID", args.targetProjectID)));
                    const targetCamerasByPlanAndNumber = new Map();
                    for (const camera of targetCameras ?? []) {
                        const key = `${camera.cameraPlanID}:${String(camera.number).trim().toLowerCase()}`;
                        const cameras = targetCamerasByPlanAndNumber.get(key) ?? [];
                        cameras.push(camera);
                        targetCamerasByPlanAndNumber.set(key, cameras);
                    }
                    const sourceCameras = await tx.run(appZql.cameras.whereExists("cameraPlan", (plan) => plan.where("projectID", args.sourceProjectID)));
                    for (const sourceCamera of sourceCameras ?? []) {
                        if (!sourceCamera.equipmentGroupID)
                            continue;
                        const targetGroupID = groupIDBySourceID.get(sourceCamera.equipmentGroupID);
                        if (!targetGroupID)
                            continue;
                        const sourcePlan = sourcePlanByID.get(sourceCamera.cameraPlanID);
                        if (!sourcePlan)
                            continue;
                        const shiftedEventDate = shiftDate(sourcePlan.eventDate, offset);
                        const targetPlanIDs = (shiftedEventDate !== null &&
                            targetPlanIDsByDate.get(shiftedEventDate)) ||
                            targetPlanIDsByName.get(String(sourcePlan.name).trim().toLowerCase()) ||
                            [];
                        if (targetPlanIDs.length !== 1)
                            continue;
                        const key = `${targetPlanIDs[0]}:${String(sourceCamera.number).trim().toLowerCase()}`;
                        const matchingCameras = targetCamerasByPlanAndNumber.get(key) ?? [];
                        if (matchingCameras.length !== 1)
                            continue;
                        await tx.mutate.cameras.update({
                            id: matchingCameras[0].id,
                            equipmentGroupID: targetGroupID,
                        });
                    }
                }
            }),
        },
    };
}
